// src/pages/Admin.jsx
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../services/supabaseClient';

const ADMIN_PASSWORD  = 'green123';
const STATUS_OPTIONS  = ['Pending', 'Confirmed', 'Completed', 'Cancelled'];
const ORDER_STATUS_OPTIONS = ['Pending', 'Packed', 'Out for Delivery', 'Delivered', 'Cancelled'];
const STATUS_COLORS   = {
  Pending:   'bg-yellow-100 text-yellow-800',
  Confirmed: 'bg-blue-100 text-blue-800',
  Completed: 'bg-green-100 text-[#2e7d32]',
  Cancelled: 'bg-red-100 text-red-700',
};
const ORDER_STATUS_COLORS = {
  Pending:          'bg-yellow-100 text-yellow-800',
  Packed:           'bg-blue-100 text-blue-800',
  'Out for Delivery':'bg-purple-100 text-purple-700',
  Delivered:        'bg-green-100 text-[#2e7d32]',
  Cancelled:        'bg-red-100 text-red-700',
};
const CATEGORIES = ["Plants", "Pots & Planters", "Fertilizers", "Cocopeat & Soil", "Pest Control"];
const TABS = [
  { id: 'bookings', label: '📅 Bookings' },
  { id: 'orders',   label: '🛒 Orders'   },
  { id: 'products', label: '🌿 Products' },
  { id: 'contacts', label: '💬 Contacts' },
];

export default function Admin() {
  const [authed,   setAuthed]   = useState(false);
  const [password, setPassword] = useState('');
  const [authErr,  setAuthErr]  = useState('');
  const [tab,      setTab]      = useState('bookings');

  // Bookings
  const [bookings,      setBookings]      = useState([]);
  const [bLoading,      setBLoading]      = useState(false);
  const [bErr,          setBErr]          = useState('');
  const [updating,      setUpdating]      = useState(null);
  const [bookingSearch, setBookingSearch] = useState('');

  // Orders
  const [orders,       setOrders]       = useState([]);
  const [oLoading,     setOLoading]     = useState(false);
  const [oErr,         setOErr]         = useState('');
  const [orderSearch,  setOrderSearch]  = useState('');
  const [oUpdating,    setOUpdating]    = useState(null);
  const [expandedOrder,setExpandedOrder]= useState(null);

  // Products
  const [products,      setProducts]      = useState([]);
  const [pLoading,      setPLoading]      = useState(false);
  const [pErr,          setPErr]          = useState('');
  const [showForm,      setShowForm]      = useState(false);
  const [editProduct,   setEditProduct]   = useState(null);
  const [pSaving,       setPSaving]       = useState(false);
  const [imageFile,     setImageFile]     = useState(null);
  const [imagePreview,  setImagePreview]  = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [catFilter,     setCatFilter]     = useState('All');
  const [stockFilter,   setStockFilter]   = useState('All');
  const [pForm,         setPForm]         = useState({
    name: '', price: '', category: 'Indoor', tag: '', description: '', image_url: '', in_stock: true,
  });

  // Contacts
  const [contacts,      setContacts]      = useState([]);
  const [cLoading,      setCLoading]      = useState(false);
  const [cErr,          setCErr]          = useState('');
  const [contactSearch, setContactSearch] = useState('');

  const stats = {
    total:     bookings.length,
    pending:   bookings.filter(b => b.status === 'Pending').length,
    completed: bookings.filter(b => b.status === 'Completed').length,
    orders:    orders.length,
  };

  // Fetch functions
  const fetchBookings = useCallback(async () => {
    setBLoading(true); setBErr('');
    try {
      const { data, error } = await supabase.from('bookings').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      setBookings(data || []);
    } catch { setBErr('Could not load bookings.'); }
    finally { setBLoading(false); }
  }, []);

  const fetchOrders = useCallback(async () => {
    setOLoading(true); setOErr('');
    try {
      const { data, error } = await supabase.from('orders').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      setOrders(data || []);
    } catch { setOErr('Could not load orders.'); }
    finally { setOLoading(false); }
  }, []);

  const fetchProducts = useCallback(async () => {
    setPLoading(true); setPErr('');
    try {
      const { data, error } = await supabase.from('products').select('*').order('name', { ascending: true });
      if (error) throw error;
      setProducts(data || []);
    } catch { setPErr('Could not load products.'); }
    finally { setPLoading(false); }
  }, []);

  const fetchContacts = useCallback(async () => {
    setCLoading(true); setCErr('');
    try {
      const { data, error } = await supabase.from('contacts').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      setContacts(data || []);
    } catch { setCErr('Could not load contacts.'); }
    finally { setCLoading(false); }
  }, []);

  useEffect(() => {
    if (!authed) return;
    fetchBookings(); fetchOrders(); fetchProducts(); fetchContacts();
  }, [authed, fetchBookings, fetchOrders, fetchProducts, fetchContacts]);

  // Filtered lists
  const filteredBookings = bookings.filter(b =>
    bookingSearch === '' ||
    b.name.toLowerCase().includes(bookingSearch.toLowerCase()) ||
    b.phone.includes(bookingSearch) ||
    (b.package || '').toLowerCase().includes(bookingSearch.toLowerCase())
  );

  const filteredOrders = orders.filter(o =>
    orderSearch === '' ||
    o.customer_name.toLowerCase().includes(orderSearch.toLowerCase()) ||
    o.customer_phone.includes(orderSearch) ||
    (o.payment_id || '').toLowerCase().includes(orderSearch.toLowerCase())
  );

  const filteredProducts = products.filter(p => {
    const matchSearch = productSearch === '' ||
      p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
      (p.tag || '').toLowerCase().includes(productSearch.toLowerCase()) ||
      p.category.toLowerCase().includes(productSearch.toLowerCase());
    const matchCat   = catFilter === 'All' || p.category === catFilter;
    const matchStock = stockFilter === 'All' ||
      (stockFilter === 'In Stock' && p.in_stock) ||
      (stockFilter === 'Hidden' && !p.in_stock);
    return matchSearch && matchCat && matchStock;
  });

  const filteredContacts = contacts.filter(c =>
    contactSearch === '' ||
    c.name.toLowerCase().includes(contactSearch.toLowerCase()) ||
    c.email.toLowerCase().includes(contactSearch.toLowerCase()) ||
    c.message.toLowerCase().includes(contactSearch.toLowerCase())
  );

  // Update booking status
  const updateStatus = async (id, newStatus) => {
    setUpdating(id);
    try {
      const { error } = await supabase.from('bookings').update({ status: newStatus }).eq('id', id);
      if (error) throw error;
      setBookings(prev => prev.map(b => b.id === id ? { ...b, status: newStatus } : b));
    } catch { alert('Failed to update status.'); }
    finally { setUpdating(null); }
  };

  // Update order status
  const updateOrderStatus = async (id, newStatus) => {
    setOUpdating(id);
    try {
      const { error } = await supabase.from('orders').update({ order_status: newStatus }).eq('id', id);
      if (error) throw error;
      setOrders(prev => prev.map(o => o.id === id ? { ...o, order_status: newStatus } : o));
    } catch { alert('Failed to update order status.'); }
    finally { setOUpdating(null); }
  };

  const markContactRead = async (id) => {
    try {
      await supabase.from('contacts').update({ status: 'Read' }).eq('id', id);
      setContacts(prev => prev.map(c => c.id === id ? { ...c, status: 'Read' } : c));
    } catch { alert('Failed to update contact.'); }
  };

  // Product helpers
  const openAddForm = () => {
    setEditProduct(null);
    setPForm({ name: '', price: '', category: 'Indoor', tag: '', description: '', image_url: '', in_stock: true });
    setImageFile(null); setImagePreview(''); setShowForm(true);
  };

  const openEditForm = (product) => {
    setEditProduct(product);
    setPForm({ name: product.name, price: product.price, category: product.category,
      tag: product.tag || '', description: product.description || '',
      image_url: product.image_url || '', in_stock: product.in_stock });
    setImageFile(null); setImagePreview(product.image_url || ''); setShowForm(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0]; if (!file) return;
    setImageFile(file); setImagePreview(URL.createObjectURL(file));
  };

  const uploadImage = async (file) => {
    const fileName = `${Date.now()}.${file.name.split('.').pop()}`;
    const { error } = await supabase.storage.from('product-images').upload(fileName, file);
    if (error) throw error;
    return supabase.storage.from('product-images').getPublicUrl(fileName).data.publicUrl;
  };

  const handleSaveProduct = async () => {
    if (!pForm.name.trim()) { alert('Plant name is required.'); return; }
    if (!pForm.price || Number(pForm.price) <= 0) { alert('Valid price is required.'); return; }
    setPSaving(true);
    try {
      let imageUrl = pForm.image_url;
      if (imageFile) imageUrl = await uploadImage(imageFile);
      const productData = { name: pForm.name.trim(), price: Number(pForm.price), category: pForm.category,
        tag: pForm.tag.trim(), description: pForm.description.trim(), image_url: imageUrl, in_stock: pForm.in_stock };
      if (editProduct) {
        const { error } = await supabase.from('products').update(productData).eq('id', editProduct.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('products').insert([productData]);
        if (error) throw error;
      }
      await fetchProducts();
      setShowForm(false); setEditProduct(null); setImageFile(null); setImagePreview('');
    } catch (err) { alert('Failed to save: ' + err.message); }
    finally { setPSaving(false); }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      const { error } = await supabase.from('products').delete().eq('id', id);
      if (error) throw error;
      setProducts(prev => prev.filter(p => p.id !== id));
    } catch (err) { alert('Failed to delete: ' + err.message); }
  };

  const toggleStock = async (id, currentStock) => {
    try {
      const { error } = await supabase.from('products').update({ in_stock: !currentStock }).eq('id', id);
      if (error) throw error;
      setProducts(prev => prev.map(p => p.id === id ? { ...p, in_stock: !currentStock } : p));
    } catch { alert('Failed to update stock.'); }
  };

  const SearchBar = ({ value, onChange, placeholder }) => (
    <div className="relative">
      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
      </svg>
      <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="w-full pl-9 pr-8 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2e7d32]/30 focus:border-[#2e7d32] bg-white" />
      {value && <button onClick={() => onChange('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg">×</button>}
    </div>
  );

  // Login
  if (!authed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 pt-16">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 w-full max-w-sm">
          <div className="text-center mb-6">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg viewBox="0 0 24 24" fill="none" stroke="#2e7d32" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Admin Login</h2>
            <p className="text-sm text-gray-400 mt-1">Enter the admin password to continue</p>
          </div>
          {authErr && <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">{authErr}</div>}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
              <input type="password" value={password}
                onChange={e => { setPassword(e.target.value); setAuthErr(''); }}
                onKeyDown={e => e.key === 'Enter' && (password === ADMIN_PASSWORD ? setAuthed(true) : setAuthErr('Incorrect password.'))}
                placeholder="Enter password"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2e7d32]/30 focus:border-[#2e7d32]" />
            </div>
            <button onClick={() => password === ADMIN_PASSWORD ? setAuthed(true) : setAuthErr('Incorrect password.')}
              className="w-full py-3 bg-[#2e7d32] text-white font-semibold rounded-xl hover:bg-[#1b5e20] transition-colors">
              Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="bg-green-50 border-b border-green-100 text-center py-2 text-sm text-gray-600">
        Serving <span className="font-semibold text-gray-800">Ulwe, Navi Mumbai - 410206</span>
      </div>
      <div className="max-w-6xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-sm text-gray-500 mt-0.5">Manage bookings, orders, products and contacts</p>
          </div>
          <button onClick={() => setAuthed(false)} className="px-4 py-2 border border-red-200 bg-white text-sm font-medium text-red-500 rounded-xl hover:bg-red-50 transition-colors">Logout</button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Bookings', value: stats.total,     color: 'text-gray-900',   bg: 'bg-white' },
            { label: 'Pending',        value: stats.pending,   color: 'text-yellow-600', bg: 'bg-yellow-50' },
            { label: 'Total Orders',   value: stats.orders,    color: 'text-purple-600', bg: 'bg-purple-50' },
            { label: 'Products',       value: products.length, color: 'text-blue-600',   bg: 'bg-blue-50' },
          ].map(({ label, value, color, bg }) => (
            <div key={label} className={`${bg} rounded-2xl border border-gray-100 shadow-sm p-5`}>
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{label}</p>
              <p className={`text-3xl font-bold ${color}`}>{value}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white rounded-xl p-1 border border-gray-100 shadow-sm overflow-x-auto">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap
                ${tab === t.id ? 'bg-[#2e7d32] text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── BOOKINGS TAB ── */}
        {tab === 'bookings' && (
          <div>
            <div className="flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center mb-5">
              <h2 className="font-bold text-gray-900">All Bookings <span className="text-sm text-gray-400 font-normal">({filteredBookings.length})</span></h2>
              <div className="flex gap-2 w-full sm:w-auto">
                <div className="flex-1 sm:w-64"><SearchBar value={bookingSearch} onChange={setBookingSearch} placeholder="Search name, phone, package..." /></div>
                <button onClick={fetchBookings} disabled={bLoading} className="px-4 py-2 border border-gray-200 bg-white text-sm font-medium text-gray-600 rounded-xl hover:bg-gray-50">↻</button>
              </div>
            </div>
            {bErr && <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">{bErr}</div>}
            {bLoading ? <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-[#2e7d32] border-t-transparent rounded-full animate-spin" /></div>
            : filteredBookings.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
                <div className="text-5xl mb-3">{bookingSearch ? '🔍' : '📋'}</div>
                <p className="text-gray-500">{bookingSearch ? `No results for "${bookingSearch}"` : 'No bookings yet.'}</p>
                {bookingSearch && <button onClick={() => setBookingSearch('')} className="mt-3 text-sm text-[#2e7d32] underline">Clear search</button>}
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50/60">
                      {['Name','Phone','Package','Pots','Date','Time','Status','Update'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filteredBookings.map(b => (
                      <tr key={b.id} className="hover:bg-gray-50/60 transition-colors">
                        <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">{b.name}</td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{b.phone}</td>
                        <td className="px-4 py-3 text-gray-600">{b.package || '—'}</td>
                        <td className="px-4 py-3 text-gray-600 text-center">{b.num_pots}</td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{new Date(b.preferred_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap text-xs">{b.preferred_time || '—'}</td>
                        <td className="px-4 py-3"><span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[b.status] || 'bg-gray-100 text-gray-600'}`}>{b.status}</span></td>
                        <td className="px-4 py-3">
                          {updating === b.id ? <div className="w-5 h-5 border-2 border-[#2e7d32] border-t-transparent rounded-full animate-spin" />
                            : <select value={b.status} onChange={e => updateStatus(b.id, e.target.value)} className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none bg-white">
                                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                              </select>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── ORDERS TAB ── */}
        {tab === 'orders' && (
          <div>
            <div className="flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center mb-5">
              <h2 className="font-bold text-gray-900">Plant Orders <span className="text-sm text-gray-400 font-normal">({filteredOrders.length})</span></h2>
              <div className="flex gap-2 w-full sm:w-auto">
                <div className="flex-1 sm:w-64"><SearchBar value={orderSearch} onChange={setOrderSearch} placeholder="Search name, phone, payment ID..." /></div>
                <button onClick={fetchOrders} disabled={oLoading} className="px-4 py-2 border border-gray-200 bg-white text-sm font-medium text-gray-600 rounded-xl hover:bg-gray-50">↻</button>
              </div>
            </div>
            {oErr && <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">{oErr}</div>}
            {oLoading ? <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-[#2e7d32] border-t-transparent rounded-full animate-spin" /></div>
            : filteredOrders.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
                <div className="text-5xl mb-3">{orderSearch ? '🔍' : '🛒'}</div>
                <p className="text-gray-500">{orderSearch ? `No results for "${orderSearch}"` : 'No orders yet.'}</p>
                {orderSearch && <button onClick={() => setOrderSearch('')} className="mt-3 text-sm text-[#2e7d32] underline">Clear search</button>}
              </div>
            ) : (
              <div className="space-y-3">
                {filteredOrders.map(o => (
                  <div key={o.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    {/* Order header */}
                    <div className="flex items-center justify-between p-5 cursor-pointer hover:bg-gray-50/60 transition-colors"
                      onClick={() => setExpandedOrder(expandedOrder === o.id ? null : o.id)}>
                      <div className="flex items-center gap-4">
                        <div>
                          <div className="flex items-center gap-2 mb-0.5">
                            <p className="font-bold text-gray-900">{o.customer_name}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${ORDER_STATUS_COLORS[o.order_status] || 'bg-gray-100 text-gray-600'}`}>
                              {o.order_status}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                            <span>📞 {o.customer_phone}</span>
                            {o.customer_email && <span>✉️ {o.customer_email}</span>}
                            <span>🕐 {new Date(o.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                            <span>💳 {o.payment_id?.slice(0,15)}...</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-[#2e7d32] text-lg">₹{o.total_amount}</span>
                        <span className="text-gray-400">{expandedOrder === o.id ? '▲' : '▼'}</span>
                      </div>
                    </div>

                    {/* Expanded order details */}
                    {expandedOrder === o.id && (
                      <div className="border-t border-gray-100 p-5 bg-gray-50/50">
                        {/* Items */}
                        <h4 className="font-semibold text-gray-800 mb-3 text-sm">Order Items</h4>
                        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden mb-4">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-gray-50 border-b border-gray-100">
                                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Plant</th>
                                <th className="px-4 py-2 text-center text-xs font-semibold text-gray-500">Qty</th>
                                <th className="px-4 py-2 text-center text-xs font-semibold text-gray-500">Price</th>
                                <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500">Subtotal</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                              {(o.items || []).map((item, idx) => (
                                <tr key={idx}>
                                  <td className="px-4 py-2.5 font-medium text-gray-900">{item.product_name}</td>
                                  <td className="px-4 py-2.5 text-center text-gray-600">{item.qty}</td>
                                  <td className="px-4 py-2.5 text-center text-gray-600">₹{item.price}</td>
                                  <td className="px-4 py-2.5 text-right font-semibold text-[#2e7d32]">₹{item.subtotal}</td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="bg-gray-50 border-t border-gray-100">
                                <td colSpan="3" className="px-4 py-2.5 font-bold text-gray-900">Total</td>
                                <td className="px-4 py-2.5 text-right font-bold text-[#2e7d32] text-base">₹{o.total_amount}</td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>

                        {/* Delivery info */}
                        {o.delivery_address && (
                          <div className="mb-4 text-sm text-gray-600">
                            <span className="font-semibold text-gray-800">📍 Delivery Address: </span>{o.delivery_address}
                          </div>
                        )}

                        {/* Update status */}
                        <div className="flex items-center gap-3">
                          <label className="text-sm font-semibold text-gray-700">Update Status:</label>
                          {oUpdating === o.id
                            ? <div className="w-5 h-5 border-2 border-[#2e7d32] border-t-transparent rounded-full animate-spin" />
                            : <select value={o.order_status} onChange={e => updateOrderStatus(o.id, e.target.value)}
                                className="px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2e7d32]/30 bg-white">
                                {ORDER_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                              </select>
                          }
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── PRODUCTS TAB ── */}
        {tab === 'products' && (
          <div>
            <div className="flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center mb-5">
              <h2 className="font-bold text-gray-900">All Products <span className="text-sm text-gray-400 font-normal">({filteredProducts.length} of {products.length})</span></h2>
              <div className="flex gap-2">
                <a href="/admin/import" className="flex items-center gap-1.5 px-4 py-2 border border-[#2e7d32] text-[#2e7d32] text-sm font-semibold rounded-xl hover:bg-green-50 transition-colors whitespace-nowrap">📦 Bulk Import</a>
                <button onClick={openAddForm} className="flex items-center gap-1.5 px-4 py-2 bg-[#2e7d32] text-white text-sm font-semibold rounded-xl hover:bg-[#1b5e20] transition-colors whitespace-nowrap">+ Add Plant</button>
              </div>
            </div>

            {/* Search + Filters */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-5">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1"><SearchBar value={productSearch} onChange={setProductSearch} placeholder="Search by name, category, tag..." /></div>
                <select value={catFilter} onChange={e => setCatFilter(e.target.value)} className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none bg-white">
                  <option value="All">All Categories</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={stockFilter} onChange={e => setStockFilter(e.target.value)} className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none bg-white">
                  <option value="All">All Stock</option>
                  <option value="In Stock">In Stock</option>
                  <option value="Hidden">Hidden</option>
                </select>
                {(productSearch || catFilter !== 'All' || stockFilter !== 'All') && (
                  <button onClick={() => { setProductSearch(''); setCatFilter('All'); setStockFilter('All'); }} className="px-3 py-2.5 border border-red-200 text-red-500 text-sm rounded-xl hover:bg-red-50 whitespace-nowrap">✕ Clear</button>
                )}
              </div>
            </div>

            {pErr && <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">{pErr}</div>}

            {/* Add/Edit Form */}
            {showForm && (
              <div className="bg-white rounded-2xl border border-green-200 shadow-sm p-6 mb-6">
                <h3 className="font-bold text-gray-900 mb-5">{editProduct ? `✏️ Edit: ${editProduct.name}` : '➕ Add New Plant'}</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Plant Name *</label>
                    <input type="text" value={pForm.name} onChange={e => setPForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Snake Plant" className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2e7d32]/30 focus:border-[#2e7d32]" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Price (₹) *</label>
                    <input type="number" value={pForm.price} onChange={e => setPForm(f => ({ ...f, price: e.target.value }))} placeholder="299" className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2e7d32]/30 focus:border-[#2e7d32]" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Category *</label>
                    <select value={pForm.category} onChange={e => setPForm(f => ({ ...f, category: e.target.value }))} className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none bg-white">
                      {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Tag</label>
                    <input type="text" value={pForm.tag} onChange={e => setPForm(f => ({ ...f, tag: e.target.value }))} placeholder="e.g. Air Purifier" className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2e7d32]/30 focus:border-[#2e7d32]" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Plant Image</label>
                    <div className="flex items-start gap-4">
                      <div className="w-24 h-24 rounded-xl border-2 border-gray-200 overflow-hidden bg-green-50 flex items-center justify-center flex-shrink-0">
                        {imagePreview ? <img src={imagePreview} alt="preview" className="w-full h-full object-cover" /> : <span className="text-3xl">🌿</span>}
                      </div>
                      <div className="flex-1">
                        <input type="file" accept="image/*" onChange={handleImageChange} className="w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-green-50 file:text-[#2e7d32]" />
                        <p className="text-xs text-gray-400 mt-1">Or paste image URL below:</p>
                        <input type="text" value={pForm.image_url} onChange={e => { setPForm(f => ({ ...f, image_url: e.target.value })); setImagePreview(e.target.value); setImageFile(null); }} placeholder="https://..." className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none" />
                      </div>
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
                    <textarea value={pForm.description} rows={2} onChange={e => setPForm(f => ({ ...f, description: e.target.value }))} placeholder="Short description..." className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none resize-none" />
                  </div>
                  <div className="flex items-center gap-3">
                    <input type="checkbox" id="in_stock" checked={pForm.in_stock} onChange={e => setPForm(f => ({ ...f, in_stock: e.target.checked }))} className="w-4 h-4 accent-[#2e7d32]" />
                    <label htmlFor="in_stock" className="text-sm font-medium text-gray-700">In Stock (visible in shop)</label>
                  </div>
                </div>
                <div className="flex gap-3 mt-5">
                  <button onClick={handleSaveProduct} disabled={pSaving} className="px-6 py-2.5 bg-[#2e7d32] text-white font-semibold rounded-xl hover:bg-[#1b5e20] transition-colors disabled:opacity-60 text-sm">
                    {pSaving ? 'Saving...' : editProduct ? '💾 Update' : '➕ Add Product'}
                  </button>
                  <button onClick={() => { setShowForm(false); setEditProduct(null); setImageFile(null); setImagePreview(''); }} className="px-6 py-2.5 border border-gray-200 text-gray-600 font-medium rounded-xl hover:bg-gray-50 transition-colors text-sm">Cancel</button>
                </div>
              </div>
            )}

            {pLoading ? <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-[#2e7d32] border-t-transparent rounded-full animate-spin" /></div>
            : filteredProducts.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
                <div className="text-5xl mb-3">{productSearch ? '🔍' : '🌱'}</div>
                <p className="text-gray-500 mb-3">{productSearch ? `No plants found for "${productSearch}"` : 'No products yet.'}</p>
                {productSearch ? <button onClick={() => setProductSearch('')} className="text-sm text-[#2e7d32] underline">Clear search</button>
                  : <button onClick={openAddForm} className="px-4 py-2 bg-[#2e7d32] text-white text-sm font-semibold rounded-xl">+ Add Plant</button>}
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredProducts.map(p => (
                  <div key={p.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                    <div className="h-36 overflow-hidden bg-green-50">
                      {p.image_url ? <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-4xl">🌿</div>}
                    </div>
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-1">
                        <h3 className="font-bold text-gray-900 text-sm leading-tight">{p.name}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ml-1 flex-shrink-0 ${p.in_stock ? 'bg-green-100 text-[#2e7d32]' : 'bg-red-100 text-red-600'}`}>{p.in_stock ? '✓' : '✗'}</span>
                      </div>
                      <p className="text-xs text-gray-400 mb-1">{p.category}{p.tag ? ` · ${p.tag}` : ''}</p>
                      <p className="font-bold text-[#2e7d32] mb-3">₹{p.price}</p>
                      <div className="flex gap-1.5">
                        <button onClick={() => openEditForm(p)} className="flex-1 py-1.5 border border-[#2e7d32] text-[#2e7d32] text-xs font-semibold rounded-lg hover:bg-green-50">✏️ Edit</button>
                        <button onClick={() => toggleStock(p.id, p.in_stock)} className="flex-1 py-1.5 border border-gray-200 text-gray-600 text-xs font-semibold rounded-lg hover:bg-gray-50">{p.in_stock ? '🚫' : '✅'}</button>
                        <button onClick={() => handleDeleteProduct(p.id)} className="py-1.5 px-2 border border-red-200 text-red-500 text-xs font-semibold rounded-lg hover:bg-red-50">🗑️</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── CONTACTS TAB ── */}
        {tab === 'contacts' && (
          <div>
            <div className="flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center mb-5">
              <h2 className="font-bold text-gray-900">
                Contact Messages
                {contacts.filter(c => c.status === 'Unread').length > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-600 text-xs font-bold rounded-full">{contacts.filter(c => c.status === 'Unread').length} Unread</span>
                )}
              </h2>
              <div className="flex gap-2 w-full sm:w-auto">
                <div className="flex-1 sm:w-64"><SearchBar value={contactSearch} onChange={setContactSearch} placeholder="Search name, email, message..." /></div>
                <button onClick={fetchContacts} disabled={cLoading} className="px-4 py-2 border border-gray-200 bg-white text-sm font-medium text-gray-600 rounded-xl hover:bg-gray-50">↻</button>
              </div>
            </div>
            {cErr && <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">{cErr}</div>}
            {cLoading ? <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-[#2e7d32] border-t-transparent rounded-full animate-spin" /></div>
            : filteredContacts.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
                <div className="text-5xl mb-3">{contactSearch ? '🔍' : '💬'}</div>
                <p className="text-gray-500">{contactSearch ? `No results for "${contactSearch}"` : 'No messages yet.'}</p>
                {contactSearch && <button onClick={() => setContactSearch('')} className="mt-3 text-sm text-[#2e7d32] underline">Clear search</button>}
              </div>
            ) : (
              <div className="space-y-3">
                {filteredContacts.map(c => (
                  <div key={c.id} className={`bg-white rounded-2xl border shadow-sm p-5 ${c.status === 'Unread' ? 'border-[#2e7d32]' : 'border-gray-100'}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-bold text-gray-900">{c.name}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.status === 'Unread' ? 'bg-green-100 text-[#2e7d32]' : 'bg-gray-100 text-gray-500'}`}>{c.status}</span>
                        </div>
                        <div className="flex flex-wrap gap-3 text-xs text-gray-500 mb-2">
                          <span>✉️ {c.email}</span>
                          {c.phone && <span>📞 {c.phone}</span>}
                          <span>🕐 {new Date(c.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        </div>
                        <p className="text-sm text-gray-700 bg-gray-50 rounded-xl p-3">{c.message}</p>
                      </div>
                      <div className="flex flex-col gap-2 flex-shrink-0">
                        {c.status === 'Unread' && (
                          <button onClick={() => markContactRead(c.id)} className="px-3 py-1.5 bg-[#2e7d32] text-white text-xs font-semibold rounded-lg hover:bg-[#1b5e20] whitespace-nowrap">✓ Mark Read</button>
                        )}
                        <a href={`mailto:${c.email}`} className="px-3 py-1.5 border border-[#2e7d32] text-[#2e7d32] text-xs font-semibold rounded-lg hover:bg-green-50 text-center whitespace-nowrap">✉️ Reply</a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
